import React from 'react';
import {setCurrent} from 'js/tool/page/current';
import I from 'immutable';
import {setCurrent as setCurrentLegacy} from 'legacy/js/tool/page/current';
import {CurrenciesMetaData, MetaDataContextType, MetaDataQuery} from 'js/tool/metaData/types';
import {isFbAdminMetaData, isFbUsersData} from 'js/tool/metaData/typeGuards';
import {FbCurrency, Maybe} from 'graphql/generated';
import {isDefined} from 'js/reusable/types/guards';
import {AdminMetadataDocument, UserMetadataDocument} from 'js/tool/metaData/__document__/queries';
import {Nullable} from 'js/reusable/types/general';

export const MetaDataContext = React.createContext<MetaDataContextType | null>(null);

export function getMetadataQuery() {
	if (import.meta.env.REACT_APP_BUILD_TARGET === 'admin') {
		return AdminMetadataDocument;
	}

	return UserMetadataDocument;
}

export function setMetaData(data?: MetaDataQuery) {
	if (data && import.meta.env.REACT_APP_NETWORK) {
		setCurrent.network(import.meta.env.REACT_APP_NETWORK);

		if (isFbAdminMetaData(data)) {
			setCurrent.options({
				...data.fbAdminMetaData,
				network: import.meta.env.REACT_APP_NETWORK,
			});

			setCurrentLegacy.options(I.fromJS({
				...data.fbAdminMetaData,
				network: import.meta.env.REACT_APP_NETWORK,
			}));

			return true;
		}

		if (isFbUsersData(data)) {
			setCurrent.options({
				timezone: data?.localeMetadata?.timezone,
				locale:   data?.localeMetadata?.locale,
				network:  import.meta.env.REACT_APP_NETWORK,
			});

			setCurrentLegacy.options(I.fromJS({
				timezone: data?.localeMetadata?.timezone,
				locale:   data.localeMetadata?.locale,
				network:  import.meta.env.REACT_APP_NETWORK,
			}));

			return true;
		}
	}

	return false;
}

export const CURRENCIES_SESSION_STORAGE_KEY = '@internalCurrenciesMetadata';

const currencyMetaDataFallback = {
	facebookCurrency:         '',
	facebookCurrencyMinValue: 0.01,
	facebookCurrencyOffset:   100,
	facebookCurrencySymbol:   '',
	decimalPlaces:            2,
};

export function saveCurrency(input: Maybe<ReadonlyArray<Maybe<FbCurrency>>> | undefined) {
	if (!input) {
		return;
	}
	const filteredCurrencies = input.filter(isDefined);
	sessionStorage.setItem(CURRENCIES_SESSION_STORAGE_KEY, JSON.stringify(
		filteredCurrencies.reduce((accu, currency) => {
		//FIXME: replace when schema updated, non-nullable
			accu[currency.facebookCurrency!] = {
				facebookCurrency:         currency.facebookCurrency!,
				facebookCurrencyMinValue: currency.facebookCurrencyMinValue ?? 0.01,
				//FIXME: replace when schema updated, non-nullable
				facebookCurrencyOffset:   +currency.facebookCurrencyOffset!,
				facebookCurrencySymbol:   currency.facebookCurrencySymbol ?? '',
				decimalPlaces:            Math.log10(+currency.facebookCurrencyOffset!),
			};

			return accu;
		}, {} as CurrenciesMetaData)
	));
}

export function getCurrencyMetaData(currency?: Nullable<string>) {
	const currencyMetaData = sessionStorage.getItem(CURRENCIES_SESSION_STORAGE_KEY);

	if (!currencyMetaData || !currency) {
		return currencyMetaDataFallback;
	}

	return (JSON.parse(currencyMetaData)[currency] as CurrenciesMetaData[string]) ?? currencyMetaDataFallback;
}
